import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import Button from '../../../../Components/Button/Button';
import SingleLineTitle from '../../../../Components/TitleElements/SingleLineTitle';
import Help from '../../../../Components/Help/Help';
import { serviceConsumer } from '../../../../network/ServiceConsumer';
import Loader from '../../../../Components/Loader/Loader';
import HeaderOrg from '../../../Headers/HeaderOrg';
import "../../../../Components/Table.css"
import '../FeisCompetitor.css';
import sort from '../../../../Assets/managefeisGrade/sort-org.svg';
import { postPrintHandler } from '../../../../helper/printHandlers';
import { getSortOrder } from '../../../../helper/CompTableSearch';
import { groupAndSortComp } from '../../../../helper/SortSyllabus';

// Big enough to pull the whole event in one request when "Show All" is used.
const LOAD_ALL = 100000;

const FeisCompetitors = () => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const eventId = searchParams.get('id');

    const [tableArr, setTableArr] = useState([]);
    const [total, setTotal] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [hasFetched, setHasFetched] = useState(false);
    const [lastQuery, setLastQuery] = useState(''); // term the shown results are for
    const [sorterData, setSorterData] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');

    // Nothing loads on arrival — the organizer searches, or asks for everyone.
    const fetchList = async (searchVal) => {
        try {
            setLoading(true);
            setErr('');
            const url = `${process.env.REACT_APP_BASE_URL}/feis/${eventId}/dancers?page=1&limit=${LOAD_ALL}&search=${encodeURIComponent(searchVal)}`;
            const res = await serviceConsumer('GET', url);
            setTableArr(res?.result || []);
            setTotal(res?.total ?? (res?.result?.length || 0));
            setHasFetched(true);
            setLastQuery(searchVal);
        } catch (error) {
            setErr(error.messages || 'Could not load competitors');
        } finally {
            setLoading(false);
        }
    };

    const submitSearch = (e) => {
        if (e) e.preventDefault();
        if (!searchInput.trim()) return;
        fetchList(searchInput.trim());
    };

    const showAll = () => {
        setSearchInput('');
        fetchList('');
    };

    const sortCompetitorsHandler = (sorter) => {
        let order = getSortOrder(sorter, sorterData, sortOrder);
        const tableArrCopy = [...tableArr]
        order === 'asc' && sorter === 'id' && tableArrCopy.sort((a, b) => a.card_number - b.card_number);
        order === 'asc' && sorter === 'competitor' && tableArrCopy.sort((a, b) => `${a.dancerData?.lname || ''}`.localeCompare(`${b.dancerData?.lname || ''}`));
        order === 'asc' && sorter === 'school' && tableArrCopy.sort((a, b) => `${a.dancerData?.school || ''}`.localeCompare(`${b.dancerData?.school || ''}`));
        order === 'asc' && sorter === 'dob' && tableArrCopy.sort((a, b) => a.dancerData.birthday - b.dancerData.birthday);

        order === 'desc' && sorter === 'id' && tableArrCopy.sort((a, b) => b.card_number - a.card_number);
        order === 'desc' && sorter === 'competitor' && tableArrCopy.sort((a, b) => `${b.dancerData?.lname || ''}`.localeCompare(`${a.dancerData?.lname || ''}`));
        order === 'desc' && sorter === 'school' && tableArrCopy.sort((a, b) => `${b.dancerData?.school || ''}`.localeCompare(`${a.dancerData?.school || ''}`));
        order === 'desc' && sorter === 'dob' && tableArrCopy.sort((a, b) => b.dancerData.birthday - a.dancerData.birthday);

        setTableArr(tableArrCopy);
        setSorterData(sorter);
        setSortOrder(order);
    };

    const printCardHandler = async (e, dancer) => {
        const urlApi = `${process.env.REACT_APP_BASE_URL}/registration/printcard/pdf`;
        postPrintHandler(urlApi, { registrationId: dancer._id, type: 'one' }, setLoading, setErr)
    };
    const printAllCardHandler = async () => {
        const urlApi = `${process.env.REACT_APP_BASE_URL}/registration/printcard/pdf`;
        postPrintHandler(urlApi, { eventId: eventId, type: 'all' }, setLoading, setErr)
    };

    return (
        <>
            {loading && <Loader />}
            <HeaderOrg />
            <SingleLineTitle titleName="Competitor List" />
            <div className="org-body-white">
                <div className="d-flex gap-2 align-items-center mt-3">
                    <div className="org-body-title">Competitors</div>
                    {hasFetched && <div className="competitor-count ">{total}</div>}
                </div>
                <div className="mb-3 hr-org-body" />
                <div className="search-label" style={{ marginTop: '33px' }}> Search:</div>
                <div className="d-md-flex align-items-start justify-content-between" style={{ gap: '12px' }}>
                    <form onSubmit={submitSearch} className="d-flex align-items-center flex-wrap" style={{ gap: '10px' }}>
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search by name, school or number…"
                            className="searchFeisComp searchFeisComps"
                        />
                        <Button type="submit" className={searchInput.trim() ? 'filled' : 'disabled'}>Search</Button>
                        <Button type="button" className="outlined" onClick={showAll}>Show All</Button>
                    </form>
                    <Button
                        onClick={printAllCardHandler}
                        className={total > 0 ? 'text-btn p-0 mt-3 mt-md-0' : 'text-btn-disabled p-0 mt-3 mt-md-0'}
                    >
                        Print All Number cards{' '}
                    </Button>
                </div>

                {!hasFetched && !loading && (
                    <div className="d-flex justify-content-center my-5 text-center" style={{ color: '#797979' }}>
                        Search for a competitor by name, school, or number — or use “Show All” to load everyone in this event.
                    </div>
                )}

                {hasFetched && (
                    <div style={{ width: '100%', overflowX: 'scroll', marginTop: '30px', marginBottom: '24px', }}>
                        <table className="table my_table competitorTable ">
                            <tbody>
                                <tr className="header-row">
                                    <td className=" mx-5 w-10 p-25-15"> #&nbsp;
                                        <span><img className="cursor-pointer" src={sort} alt="" onClick={() => sortCompetitorsHandler('id')} /></span>
                                    </td>
                                    <td className="mx-5 w-25 p-25-5">Competitor&nbsp;
                                        <span><img className="cursor-pointer" src={sort} alt="" onClick={() => sortCompetitorsHandler('competitor')} /></span>
                                    </td>
                                    <td className="w-20 p-25-5">School Name&nbsp;
                                        <span><img className="cursor-pointer" src={sort} alt="" onClick={() => sortCompetitorsHandler('school')} /></span>
                                    </td>
                                    <td className="w-10 p-25-5">DOB&nbsp;
                                        <span><img className="cursor-pointer" src={sort} alt="" onClick={() => sortCompetitorsHandler('dob')} /></span>
                                    </td>
                                    <td className="w-20 p-25-5">Competitions</td>
                                    <td className="w-15 p-25-5 text-center">Card</td>
                                </tr>
                                {tableArr.map((dancer) => {
                                    return (
                                        <tr key={uuidv4()} style={{ background: '#FFFFFF', fontSize: '12px', height: '31px', }}>
                                            <td className="mx-5 py-4 p-25-15 w-10 grey-30">{dancer?.card_number}</td>
                                            <td className="mx-5 w-25 grey-30">
                                                <div> {dancer?.dancerData?.fname}&nbsp; {dancer?.dancerData?.lname}</div>
                                            </td>
                                            <td className="w-20 grey-30">{dancer?.dancerData?.school}</td>
                                            <td className="w-10 grey-30">{dancer?.dancerData?.birthday}</td>
                                            <td className="w-20 grey-30">
                                                {dancer?.competitonData && groupAndSortComp(dancer.competitonData).map((item, index) => {
                                                    return dancer?.competitonData?.length === index + 1 ? `${item?.code}` : `${item?.code}, `;
                                                })}
                                            </td>
                                            <td className="w-15 grey-30 text-center" >
                                                <Button onClick={(e) => printCardHandler(e, dancer)} className="text-btn p-0 text-btn-tab">Number card</Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {!loading && total === 0 && lastQuery !== '' && (
                            <div className="d-flex justify-content-center my-5"> No competitors match “{lastQuery}”.</div>
                        )}
                        {!loading && total === 0 && lastQuery === '' && (
                            <div className="d-flex justify-content-center my-5">There are no competitors registered for this event</div>
                        )}
                        {!loading && err && (
                            <div className="d-flex justify-content-center error-class">{err}</div>
                        )}
                    </div>
                )}
            </div>
            <Help />
        </>
    );
};

export default FeisCompetitors;
